import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Header from "../components/Header";
import toast from "react-hot-toast";
import { FaSpinner, FaStar } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const ratingLabels = {
  1: "Poor",
  2: "Fair",
  3: "Good",
  4: "Very good",
  5: "Excellent",
};

export default function Feedback() {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillError, setPrefillError] = useState("");
  const [prefilledBookingId, setPrefilledBookingId] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const feedbackToken = queryParams.get("token") || "";
  const bookingId =
    queryParams.get("bookingId") ||
    queryParams.get("booking_id") ||
    queryParams.get("booking") ||
    "";
  const bookingReference = (prefilledBookingId || bookingId).trim();
  const isMissingBookingReference = !bookingReference;

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim(),
    email: Yup.string().trim().email("Enter a valid email address"),
    comments: Yup.string()
      .trim()
      .required("Please leave a comment to let us know how we did."),
  });

  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: "",
      email: "",
      comments: "",
    },
  };
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm(formOptions);

  useEffect(() => {
    document.title = "Leave Feedback - Thorney Park Golf Club";
  }, []);

  useEffect(() => {
    const fetchFeedbackPrefill = async () => {
      if (!feedbackToken) {
        setPrefilledBookingId("");
        setPrefillError("");
        return;
      }

      setPrefillLoading(true);
      setPrefillError("");

      try {
        const apiUrl =
          process.env.REACT_APP_API_URL || "http://localhost:1337/api/";
        const response = await axios.get(`${apiUrl}feedback-prefill`, {
          params: { token: feedbackToken },
        });

        if (!response.data.success || !response.data.data) {
          throw new Error("Invalid feedback prefill response");
        }

        const { bookingId: apiBookingId, name, email } = response.data.data;

        setPrefilledBookingId(apiBookingId ? String(apiBookingId) : "");
        reset({
          name: name || "",
          email: email || "",
          comments: "",
        });
      } catch (error) {
        console.error("Feedback prefill error:", error);
        setPrefilledBookingId("");
        setPrefillError(
          "We could not verify this feedback link. Please request a new link or contact the club.",
        );
      } finally {
        setPrefillLoading(false);
      }
    };

    fetchFeedbackPrefill();
  }, [feedbackToken, reset]);

  const submitFeedback = async (data) => {
    if (isMissingBookingReference) {
      toast.error("Booking reference is required to submit feedback.");
      return;
    }

    if (rating === 0) {
      toast.error("Please provide a star rating.");
      return;
    }

    setFormSubmitted(true);

    const payload = {
      rating,
      comments: data.comments.trim(),
      name: data.name ? data.name.trim() : "",
      email: data.email ? data.email.trim() : "",
      bookingId: bookingReference,
      ...(feedbackToken ? { token: feedbackToken } : {}),
    };

    try {
      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:1337/api/";
      const response = await axios.post(`${apiUrl}submitFeedback`, payload);

      if (response.data.success) {
        const thankYou = response.data.thankYou || null;
        try {
          sessionStorage.setItem(
            "feedbackThankYou",
            JSON.stringify({
              bookingReference,
              feedbackToken,
              thankYou,
            }),
          );
        } catch (storageError) {
          console.warn("Unable to cache feedback thank-you details:", storageError);
        }
        toast.success("Thank you! Your feedback has been submitted.", {
          style: { borderRadius: "10px", background: "#333", color: "#fff" },
          iconTheme: { primary: "#4BB543", secondary: "#FFF" },
        });
        reset();
        setRating(0);
        setHoverRating(0);
        const thankYouPath = feedbackToken
          ? `/thankyou?token=${encodeURIComponent(feedbackToken)}`
          : "/thankyou";
        navigate(thankYouPath, {
          replace: true,
          state: { bookingReference, feedbackToken, thankYou },
        });
      } else {
        throw new Error(
          response.data.message || "Failed to submit feedback",
        );
      }
    } catch (error) {
      console.error("Feedback error:", error);
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        "Failed to submit feedback. Please try again.";
      toast.error(errorMessage);
    } finally {
      setFormSubmitted(false);
    }
  };

  return (
    <>
      <Header />
      <main className="bg-gray-50 px-4 py-8 sm:px-6 lg:px-8">
        <section className="mx-auto max-w-5xl overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-200">
          <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="bg-[#0f6f2f] px-6 py-8 text-white sm:px-8 lg:px-10">
              <p className="text-sm font-semibold uppercase tracking-wide text-green-100">
                Guest feedback
              </p>
              <h1 className="mt-3 text-3xl font-bold leading-tight">
                How was your visit to Thorney Park?
              </h1>
              <p className="mt-4 text-base leading-7 text-green-50">
                Share a quick rating and a few notes about your experience. The
                team uses every review to improve future bookings and visits.
              </p>

              <div className="mt-8 rounded-md border border-white/20 bg-white/10 p-4">
                <p className="text-sm font-semibold text-green-50">
                  Booking reference
                </p>
                <p className="mt-1 break-words text-lg font-semibold">
                  {bookingReference || "Not provided"}
                </p>
              </div>

              {isMissingBookingReference && (
                <div className="mt-5 rounded-md border border-yellow-200 bg-yellow-50 p-4 text-sm font-medium text-yellow-900">
                  {prefillLoading
                    ? "Verifying your feedback link..."
                    : "This feedback link is missing a booking reference. Please use the link from your booking email."}
                </div>
              )}

              {prefillError && (
                <div className="mt-5 rounded-md border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-900">
                  {prefillError}
                </div>
              )}

            </div>

            <form
              method="POST"
              onSubmit={handleSubmit(submitFeedback)}
              className="px-6 py-8 sm:px-8 lg:px-10"
            >
              <div className="mb-7">
                <p className="text-sm font-semibold uppercase tracking-wide text-[#0f6f2f]">
                  Your review
                </p>
                <h2 className="mt-2 text-2xl font-bold text-gray-900">
                  Tell us how we did
                </h2>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="name"
                    className="mb-2 block text-sm font-semibold text-gray-800"
                  >
                    Name{" "}
                    <span className="font-normal text-gray-500">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    {...register("name")}
                    autoComplete="name"
                    disabled={prefillLoading}
                    className="w-full rounded-md border border-gray-300 px-4 py-3 text-gray-900 shadow-sm transition focus:border-[#17BA17] focus:outline-none focus:ring-2 focus:ring-[#17BA17]/25"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-sm font-semibold text-gray-800"
                  >
                    Email{" "}
                    <span className="font-normal text-gray-500">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    {...register("email")}
                    autoComplete="email"
                    disabled={prefillLoading}
                    className={`w-full rounded-md border px-4 py-3 text-gray-900 shadow-sm transition focus:outline-none focus:ring-2 ${
                      errors.email
                        ? "border-red-400 focus:border-red-500 focus:ring-red-200"
                        : "border-gray-300 focus:border-[#17BA17] focus:ring-[#17BA17]/25"
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-7 rounded-md border border-gray-200 bg-gray-50 p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800">
                      Overall rating <span className="text-red-600">*</span>
                    </label>
                    <p className="mt-1 text-sm text-gray-500">
                      {rating
                        ? `${rating} out of 5 - ${ratingLabels[rating]}`
                        : "Select a rating"}
                    </p>
                  </div>
                  <div
                    className="flex items-center gap-1"
                    role="radiogroup"
                    aria-label="Overall rating"
                  >
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        role="radio"
                        aria-checked={rating === star}
                        aria-label={`${star} star${star > 1 ? "s" : ""}`}
                        className="rounded-full p-1 text-gray-300 transition hover:scale-105 hover:text-yellow-400 focus:outline-none focus:ring-2 focus:ring-[#17BA17]/40"
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        onFocus={() => setHoverRating(star)}
                        onBlur={() => setHoverRating(0)}
                        onClick={() => setRating(star)}
                      >
                        <FaStar
                          size={34}
                          className={
                            (hoverRating || rating) >= star
                              ? "text-yellow-400"
                              : "text-gray-300"
                          }
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-7">
                <label
                  htmlFor="comments"
                  className="mb-2 block text-sm font-semibold text-gray-800"
                >
                  Your review <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="comments"
                  rows={5}
                  {...register("comments")}
                  placeholder="Tell us about your experience..."
                  className={`w-full resize-none rounded-md border px-4 py-3 text-gray-900 shadow-sm transition focus:outline-none focus:ring-2 ${
                    errors.comments
                      ? "border-red-400 focus:border-red-500 focus:ring-red-200"
                      : "border-gray-300 focus:border-[#17BA17] focus:ring-[#17BA17]/25"
                  }`}
                />
                {errors.comments && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.comments.message}
                  </p>
                )}
              </div>

              <div className="mt-8 flex flex-col gap-3 border-t border-gray-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-500">
                  Required fields are marked with an asterisk.
                </p>
                {formSubmitted ? (
                  <button
                    type="button"
                    disabled
                    className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-[#17BA17] px-7 py-3 text-base font-semibold text-white opacity-75 shadow-sm sm:w-auto"
                  >
                    <FaSpinner className="animate-spin mr-2" />
                    Submitting...
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isMissingBookingReference || prefillLoading}
                    className={`inline-flex w-full items-center justify-center rounded-md border border-transparent px-7 py-3 text-base font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#17BA17]/40 focus:ring-offset-2 sm:w-auto ${
                      isMissingBookingReference || prefillLoading
                        ? "cursor-not-allowed bg-gray-300"
                        : "bg-[#17BA17] hover:bg-[#129c12]"
                    }`}
                  >
                    Submit Feedback
                  </button>
                )}
              </div>
            </form>
          </div>
        </section>
      </main>
    </>
  );
}
